/**
 * An app's setup questions — a licence key, the edition to install, the
 * player who owns a game server, the vendor's terms — as every surface of the
 * console asks them: the checkout's "Configure <app>" step, the reinstall
 * dialog and the instance's "App settings" card.
 *
 * The API declares the questions (`inputs` on a catalog template, and on
 * `GET /vm/:id/templates`) and is the ONE judge of an answer
 * (src/templates/app-inputs/app-inputs.ts there). This mirror exists so the
 * customer hears about a wrong answer beside the field, as they type — never
 * as a refusal after the click. Its verdict must never be stricter than the
 * API's: a field the API would take is never blocked here.
 *
 * Pure, and erasable-syntax only, so `node --test` imports it as it is.
 */

export type AppInputType = 'text' | 'secret' | 'select' | 'toggle' | 'number' | 'email'
export type AppInputNeed = 'required' | 'recommended' | 'optional'
export type AppInputStage = 'order' | 'reinstall' | 'instance_page'

export interface AppInputOption {
  value: string
  recommended?: boolean
  fallback: { label: string; description?: string }
}

export interface AppInputDefinition {
  key: string
  type: AppInputType
  need: AppInputNeed
  default?: string | number | boolean
  options?: AppInputOption[]
  pattern?: string
  min_length?: number
  max_length?: number
  min?: number
  max?: number
  when?: { key: string; equals: string | boolean }
  obtain_url?: string
  after_install?: boolean
  editable?: boolean
  advanced?: boolean
  must_accept?: boolean
  fallback: {
    label: string
    help: string
    placeholder?: string
    later?: string
    steps?: string[]
  }
}

/** What a form holds: the raw control values, by key. */
export type AppInputAnswers = Record<string, string | number | boolean>

/** A field's problem, in the API's vocabulary (worded by the console). */
export interface AppInputProblem {
  code:
    | 'required'
    | 'must_accept'
    | 'wrong_type'
    | 'control_characters'
    | 'too_short'
    | 'too_long'
    | 'pattern'
    | 'not_an_option'
    | 'not_an_integer'
    | 'too_small'
    | 'too_large'
    | 'invalid_email'
    | 'unknown'
    | 'not_accepted_now'
    | 'not_editable'
  params: Record<string, string | number> | null
}

/** The API's own ceilings: a field is a line, not a document. */
export const APP_INPUT_MAX_LENGTH = 1024

/** The platform fills this default with the account holder's address. */
export const ACCOUNT_EMAIL_DEFAULT = '$account_email'

// C0 and C1 controls, DEL and the Unicode line separators — the API's set.
const CONTROL_CHARACTERS = new RegExp('[\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]')
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** The questions asked at a stage: an `after_install` one only once the server exists. */
export function appInputsAskedAt(
  defs: readonly AppInputDefinition[] | null | undefined,
  stage: AppInputStage,
): AppInputDefinition[] {
  return (defs ?? []).filter((def) => stage === 'instance_page' || !def.after_install)
}

/** Whether a template asks anything at this stage. */
export function hasAppInputs(
  defs: readonly AppInputDefinition[] | null | undefined,
  stage: AppInputStage,
): boolean {
  return appInputsAskedAt(defs, stage).length > 0
}

/** A control's value as the string the guest would receive ('' = no answer). */
export function answerString(value: string | number | boolean | null | undefined): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return typeof value === 'number' ? (Number.isFinite(value) ? String(value) : '') : value.trim()
}

/**
 * Whether a field is shown, given the answers so far. A condition reads an
 * EARLIER field, with its default when it was not touched — the API resolves
 * them in order the same way.
 */
export function isAppInputVisible(
  def: AppInputDefinition,
  defs: readonly AppInputDefinition[],
  answers: AppInputAnswers,
): boolean {
  if (!def.when) return true
  const source = defs.find((d) => d.key === def.when!.key)
  if (!source) return false
  const raw = answers[source.key]
  const value = answerString(raw) !== '' ? answerString(raw) : (defaultAnswer(source) ?? '')
  return value === String(def.when.equals)
}

/** The default a field starts from, as its string, or null. */
export function defaultAnswer(def: AppInputDefinition): string | null {
  if (def.default === undefined || def.type === 'secret') return null
  if (def.default === ACCOUNT_EMAIL_DEFAULT) return null
  if (def.type === 'toggle') return def.default === true ? 'true' : 'false'
  return String(def.default)
}

/**
 * What a form starts from. A select starts on its recommended option (which
 * is also its default), a toggle and a number on their default, a terms toggle
 * UNTICKED (nobody accepts terms by default), and text empty. `existing` is
 * what the instance holds — a reinstall of the same app starts from it; a
 * secret is never in it, the API never returns one.
 */
export function initialAppInputAnswers(
  defs: readonly AppInputDefinition[],
  existing: Record<string, string> = {},
): AppInputAnswers {
  const answers: AppInputAnswers = {}
  for (const def of defs) {
    const kept = existing[def.key]
    switch (def.type) {
      case 'toggle':
        answers[def.key] =
          kept !== undefined ? kept === 'true' : def.must_accept ? false : def.default === true
        break
      case 'number': {
        const fromKept = kept !== undefined && kept !== '' ? Number(kept) : NaN
        answers[def.key] = Number.isFinite(fromKept)
          ? fromKept
          : typeof def.default === 'number'
            ? def.default
            : ''
        break
      }
      case 'select':
        answers[def.key] = kept ?? (def.default !== undefined ? String(def.default) : '')
        break
      case 'secret':
        answers[def.key] = ''
        break
      default:
        answers[def.key] =
          kept ?? (def.default !== undefined && def.default !== ACCOUNT_EMAIL_DEFAULT ? String(def.default) : '')
    }
  }
  return answers
}

/**
 * Whether an answer may be REMOVED on the running server (sent empty): only an
 * optional text or secret the app lets change. The API's `isAppInputClearable`,
 * the same rule — a game's join password removed opens the server again.
 */
export function isAppInputClearable(def: AppInputDefinition): boolean {
  return def.need === 'optional' && def.editable === true && (def.type === 'text' || def.type === 'secret')
}

/** One answer against its field: null when it is fine (or simply empty). */
export function checkAppInputAnswer(
  def: AppInputDefinition,
  raw: string | number | boolean | null | undefined,
): AppInputProblem | null {
  const problem = (code: AppInputProblem['code'], params: Record<string, string | number> | null = null) => ({
    code,
    params,
  })
  if (def.type === 'toggle') {
    if (def.must_accept && raw !== true && raw !== 'true') return problem('must_accept')
    return null
  }
  const value = answerString(raw)
  if (value === '') return null
  switch (def.type) {
    case 'number': {
      const n = Number(value)
      if (!Number.isFinite(n)) return problem('wrong_type')
      if (!Number.isInteger(n)) return problem('not_an_integer')
      if (def.min !== undefined && n < def.min) return problem('too_small', { min: def.min })
      if (def.max !== undefined && n > def.max) return problem('too_large', { max: def.max })
      return null
    }
    case 'select':
      return (def.options ?? []).some((o) => o.value === value) ? null : problem('not_an_option')
    default: {
      if (CONTROL_CHARACTERS.test(value)) return problem('control_characters')
      const max = Math.min(def.max_length ?? APP_INPUT_MAX_LENGTH, APP_INPUT_MAX_LENGTH)
      if (value.length > max) return problem('too_long', { max })
      if (def.min_length !== undefined && value.length < def.min_length) {
        return problem('too_short', { min: def.min_length })
      }
      if (def.type === 'email' && !EMAIL.test(value)) return problem('invalid_email')
      if (def.pattern) {
        try {
          if (!new RegExp(def.pattern, 'u').test(value)) return problem('pattern')
        } catch {
          // A pattern this browser cannot compile is the API's to judge.
        }
      }
      return null
    }
  }
}

export interface AppInputsVerdict {
  /** Problems by key, for the fields on screen. */
  problems: Record<string, AppInputProblem>
  /** Required fields still unanswered (a subset of `problems`). */
  missing: string[]
  /** Recommended fields left for later: the app installs and waits for them. */
  later: string[]
}

/**
 * The whole form's verdict at a stage. `kept` names the fields whose current
 * answer the instance already holds (a reinstall of the same app keeps a
 * secret without it being typed again): an empty control there is not missing.
 */
export function validateAppInputAnswers(
  defs: readonly AppInputDefinition[],
  answers: AppInputAnswers,
  options: { stage: AppInputStage; kept?: ReadonlySet<string>; accountEmail?: string | null } = { stage: 'order' },
): AppInputsVerdict {
  const problems: Record<string, AppInputProblem> = {}
  const missing: string[] = []
  const later: string[] = []
  for (const def of appInputsAskedAt(defs, options.stage)) {
    if (!isAppInputVisible(def, defs, answers)) continue
    const raw = answers[def.key]
    const problem = checkAppInputAnswer(def, raw)
    if (problem) {
      problems[def.key] = problem
      if (problem.code === 'must_accept') missing.push(def.key)
      continue
    }
    if (def.type === 'toggle') continue
    if (answerString(raw) !== '' || options.kept?.has(def.key)) continue
    if (def.default === ACCOUNT_EMAIL_DEFAULT && options.accountEmail) continue
    if (defaultAnswer(def) !== null) continue
    if (def.need === 'required') {
      problems[def.key] = { code: 'required', params: null }
      missing.push(def.key)
    } else if (def.need === 'recommended') {
      later.push(def.key)
    }
  }
  return { problems, missing, later }
}

/**
 * The body's `app_inputs`: every visible field the customer answered, as they
 * answered it (text trimmed). An empty control is left out — the API then
 * keeps what the instance holds (a reinstall of the same app), applies the
 * field's default, or waits for it — and so is a hidden or after-install one.
 */
export function appInputsPayload(
  defs: readonly AppInputDefinition[],
  answers: AppInputAnswers,
  stage: AppInputStage,
): Record<string, string | number | boolean> {
  const payload: Record<string, string | number | boolean> = {}
  for (const def of appInputsAskedAt(defs, stage)) {
    if (!isAppInputVisible(def, defs, answers)) continue
    const raw = answers[def.key]
    if (def.type === 'toggle') {
      if (raw === true || raw === false) payload[def.key] = raw
      continue
    }
    if (def.type === 'number') {
      const value = answerString(raw)
      if (value !== '' && Number.isFinite(Number(value))) payload[def.key] = Number(value)
      continue
    }
    const value = answerString(raw)
    if (value !== '') payload[def.key] = value
  }
  return payload
}

/**
 * The answers worth keeping in a checkout draft: never a secret — the draft
 * lives in the tab's storage and a licence key is not the tab's to keep.
 */
export function draftableAppInputAnswers(
  defs: readonly AppInputDefinition[],
  answers: AppInputAnswers,
): AppInputAnswers {
  const kept: AppInputAnswers = {}
  for (const def of defs) {
    if (def.type === 'secret') continue
    const raw = answers[def.key]
    if (raw !== undefined && answerString(raw) !== '') kept[def.key] = raw
  }
  return kept
}

/** Whether a draft left a secret behind that the customer must type again. */
export function secretsNeedingReentry(
  defs: readonly AppInputDefinition[],
  answers: AppInputAnswers,
): string[] {
  return defs
    .filter((def) => def.type === 'secret' && !def.after_install && isAppInputVisible(def, defs, answers))
    .filter((def) => answerString(answers[def.key]) === '')
    .map((def) => def.key)
}

/** The API's refusal of a set of answers, by key — from an APP_INPUTS_INVALID body. */
export function appInputProblemsFromApi(body: unknown): Record<string, AppInputProblem> | null {
  const b = body as { code?: unknown; errors?: unknown } | null
  if (!b || b.code !== 'APP_INPUTS_INVALID' || !Array.isArray(b.errors)) return null
  const problems: Record<string, AppInputProblem> = {}
  for (const e of b.errors as { key?: unknown; code?: unknown; params?: unknown }[]) {
    if (typeof e?.key !== 'string' || typeof e.code !== 'string') continue
    problems[e.key] = {
      code: e.code as AppInputProblem['code'],
      params: (e.params as Record<string, string | number> | null) ?? null,
    }
  }
  return problems
}
